import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ClipboardList, Loader2, Plus, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ReferralSummary } from '../components/referral-tracker/ReferralSummary';
import { ReferralTable } from '../components/referral-tracker/ReferralTable';
import { ContactEditDialog } from '../components/referral-tracker/ContactEditDialog';
import { AddReferralDialog } from '../components/referral-tracker/AddReferralDialog';
import { RowActionDialog } from '../components/referral-tracker/RowActionDialog';
import { LinkProfileDialog } from '../components/referral-tracker/LinkProfileDialog';
import { TrackerSettingsDialog, BulkContact4Dialog } from '../components/referral-tracker/TrackerSettingsDialog';
import { Settings, UserPlus, ListChecks, ChevronDown, ChevronUp, FileSpreadsheet, Printer } from 'lucide-react';
import { printReferrals } from '../components/referral-tracker/printReferrals';
import { endsThisMonth } from '../components/referral-tracker/ReferralFieldCells';
import { MyOutreachQueue } from '../components/referral-tracker/MyOutreachQueue';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const REFERRAL_TRACKER_ROLES = ['system_admin', 'executive', 'executive_leadership', 'operations_manager', 'case_manager_supervisor', 'case_manager'];
const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' });

const Filters = ({ options, fy, setFy, month, setMonth, months }) => (
  <div className="flex flex-wrap items-end gap-4" data-testid="rt-filters">
    <div className="space-y-1.5">
      <Label>Fiscal Year</Label>
      <Select value={fy} onValueChange={(v) => { setFy(v); setMonth('_all'); }}>
        <SelectTrigger className="w-48" data-testid="rt-fy-filter"><SelectValue /></SelectTrigger>
        <SelectContent>{options.fiscal_years.map((f) => <SelectItem key={f} value={f}>FY {f.replace('-', ' - ')}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label>Month</Label>
      <Select value={month} onValueChange={setMonth}>
        <SelectTrigger className="w-48" data-testid="rt-month-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">Whole fiscal year</SelectItem>
          {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </div>
);

export default function ReferralTrackerPage() {
  const { user } = useAuth();
  const [options, setOptions] = useState(null);
  const [tab, setTab] = useState('');
  const [fy, setFy] = useState('');
  const [month, setMonth] = useState('_all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [adding, setAdding] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [linkRecord, setLinkRecord] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showBulk4, setShowBulk4] = useState(false);
  const [queue, setQueue] = useState(null);
  const [queueOpen, setQueueOpen] = useState(true);
  const [exporting, setExporting] = useState(false);
  const allowed = REFERRAL_TRACKER_ROLES.includes(user?.role);

  useEffect(() => {
    if (!allowed) return;
    axios.get(`${API}/referral-tracker/options`).then(({ data: o }) => {
      setOptions(o);
      setFy(o.current_fiscal_year);
      setTab((o.tabs.find((t) => t.id === 'snap_et_nc') || o.tabs[0])?.id || '');
    }).catch(() => toast.error('Could not load the Referral Tracker'));
  }, [allowed]);

  const load = useCallback(async () => {
    if (!tab || !fy) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referral-tracker/records`, { params: { enrollment_type_id: tab, fiscal_year: fy, month } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load referrals');
    } finally {
      setLoading(false);
    }
  }, [tab, fy, month]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referral-tracker/my-queue`);
      setQueue(res.data.items);
    } catch {
      setQueue([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (allowed) loadQueue(); }, [allowed, loadQueue]);
  const refresh = () => { load(); loadQueue(); };

  const periodLabel = () => (month === '_all' ? `FY ${fy.replace('-', ' - ')}` : monthLabel(month));
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/referral-tracker/records/export`, { params: { enrollment_type_id: tab, fiscal_year: fy, month }, responseType: 'blob' });
      const name = (res.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/)?.[1] || 'Referral_Tracker.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', name);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch {
      toast.error('Could not export');
    } finally {
      setExporting(false);
    }
  };
  const printList = () => {
    const t = options.tabs.find((x) => x.id === tab);
    if (!printReferrals(data.records, `${t?.name || ''} Referral Tracker`, periodLabel())) toast.error('Allow pop-ups to print');
  };

  const saveGoal = async (goal) => {
    try {
      await axios.put(`${API}/referral-tracker/goal`, { enrollment_type_id: tab, fiscal_year: fy, goal });
      toast.success('Enrollment goal saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save goal');
    }
  };

  if (!allowed) {
    return (
      <div className="p-6"><Card className="max-w-lg mx-auto"><CardContent className="py-10 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-destructive" />
        <p className="font-semibold" data-testid="rt-access-denied">You don't have access to the Referral Tracker.</p>
      </CardContent></Card></div>
    );
  }
  if (!options) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const activeTab = options.tabs.find((t) => t.id === tab);
  const rowCtx = { me: user?.id, isManager: options.is_manager, options, onEditContact: (record, n) => setEditTarget({ record, n }),
    onRowSaved: (rec) => setData((dd) => ({ ...dd, records: dd.records.map((x) => (x.id === rec.id ? { ...x, ...rec } : x)) })),
    onAction: (record, n, kind) => setActionTarget({ record, n, kind }), onLink: setLinkRecord };
  const defaultMonth = month !== '_all' ? month : (data?.months?.includes(options.current_month) ? options.current_month : data?.months?.[0]);

  return (
    <div className="p-6 space-y-6" data-testid="referral-tracker-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ClipboardList className="w-6 h-6 text-primary" />Referral Tracker &amp; Queue</h1>
        <p className="text-muted-foreground">SNAP E&amp;T referral outreach by enrollment type</p>
      </div>
      <Card className="card-shadow border-primary/30" data-testid="rt-myqueue">
        <CardHeader className="py-3 cursor-pointer" onClick={() => setQueueOpen((o) => !o)}>
          <CardTitle className="text-lg flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" />My Outreach Queue
            <span className="text-sm font-normal text-muted-foreground" data-testid="rt-myqueue-count">{queue ? `${queue.length} due today or overdue` : 'Loading…'}</span>
            <span className="ml-auto" data-testid="rt-myqueue-toggle">{queueOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
          </CardTitle>
        </CardHeader>
        {queueOpen && queue && <CardContent className="pt-0"><MyOutreachQueue items={queue} ctx={rowCtx} /></CardContent>}
      </Card>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setData(null); }}>
        <TabsList className="flex flex-wrap h-auto" data-testid="rt-tabs">
          {options.tabs.map((t) => <TabsTrigger key={t.id} value={t.id} data-testid={`rt-tab-${t.id}`}>{t.name.replace('SNAP E&T Program - ', '')}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Filters options={options} fy={fy} setFy={setFy} month={month} setMonth={setMonth} months={data?.months || []} />
        <div className="flex flex-wrap gap-2">
          {options.can_edit_settings && <Button variant="outline" onClick={() => setShowSettings(true)} disabled={!activeTab} data-testid="rt-settings-btn"><Settings className="w-4 h-4 mr-2" />Settings</Button>}
          {options.is_manager && month !== '_all' && <Button variant="outline" onClick={() => setShowBulk4(true)} data-testid="rt-bulk-contact4-btn"><UserPlus className="w-4 h-4 mr-2" />Add Contact 4 for Month</Button>}
          <Button variant="outline" onClick={exportExcel} disabled={!data || exporting} data-testid="rt-export-excel-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}Export to Excel
          </Button>
          <Button variant="outline" onClick={printList} disabled={!data} data-testid="rt-print-btn"><Printer className="w-4 h-4 mr-2" />Print</Button>
          <Button onClick={() => setAdding(true)} disabled={!activeTab} data-testid="rt-add-referral-btn"><Plus className="w-4 h-4 mr-2" />Add New Referral</Button>
        </div>
      </div>
      {data && <ReferralSummary summary={data.summary} onSaveGoal={saveGoal} />}
      <Card className="card-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2" data-testid="rt-list-title">
            {activeTab?.name} · {month === '_all' ? `FY ${fy.replace('-', ' - ')}` : monthLabel(month)}
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data && data.records.some(endsThisMonth) && (
            <div className="mb-3 flex items-center gap-2 text-sm rounded-md bg-red-50 text-red-800 px-3 py-2" data-testid="rt-cert-ending-note">
              <AlertTriangle className="w-4 h-4" />
              {data.records.filter(endsThisMonth).length} referral(s) have a certification period ending this month. They're highlighted in red.
            </div>
          )}
          {data && <ReferralTable records={data.records} ctx={rowCtx} />}</CardContent>
      </Card>
      <ContactEditDialog target={editTarget} options={options} me={user?.id} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); refresh(); }} />
      <RowActionDialog target={actionTarget} onClose={() => setActionTarget(null)} onCalled={(record, n) => { setActionTarget(null); setEditTarget({ record, n }); }} />
      <LinkProfileDialog record={linkRecord} onClose={() => setLinkRecord(null)} onLinked={() => { setLinkRecord(null); refresh(); }} />
      <TrackerSettingsDialog tab={activeTab} open={showSettings} onClose={() => setShowSettings(false)} />
      <BulkContact4Dialog open={showBulk4} tab={activeTab} month={month} monthLabel={month !== '_all' ? monthLabel(month) : ''} staff={options.staff}
        onClose={() => setShowBulk4(false)} onDone={() => { setShowBulk4(false); refresh(); }} />
      {activeTab && (
        <AddReferralDialog open={adding} tab={activeTab} options={{ ...options, months: data?.months }} defaultMonth={defaultMonth}
          onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refresh(); }} />
      )}
    </div>
  );
}
